import { stringify } from 'querystring';
import { type ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import { isValidActivity } from '@alltrails/explore-service/hooks/filterConfigs/useActivityFilterConfig';
import { getValidDifficulties } from '@alltrails/explore-service/hooks/filterConfigs/useDifficultyFilterConfig';
import { getValidDistanceAway } from '@alltrails/explore-service/hooks/filterConfigs/useDistanceAwayFilterConfig';
import { getValidDuration } from '@alltrails/explore-service/hooks/filterConfigs/useDurationFilterConfig';
import { getValidElevationGain } from '@alltrails/explore-service/hooks/filterConfigs/useElevationGainFilterConfig';
import { getValidFeatures } from '@alltrails/explore-service/hooks/filterConfigs/useFeatureFilterConfig';
import { getValidHighestPoint } from '@alltrails/explore-service/hooks/filterConfigs/useHighestPointFilterConfig';
import { getValidLength } from '@alltrails/explore-service/hooks/filterConfigs/useLengthFilterConfig';
import { isValidRating } from '@alltrails/explore-service/hooks/filterConfigs/useMinRatingFilterConfig';
import { getValidRouteTypes } from '@alltrails/explore-service/hooks/filterConfigs/useRouteTypeFilterConfig';
import { getValidSuitabilities } from '@alltrails/explore-service/hooks/filterConfigs/useSuitabilityFilterConfig';
import { getValidTrailEngagements } from '@alltrails/explore-service/hooks/filterConfigs/useTrailEngagementFilterConfig';
import type { NaturalLanguageSearchAnalyticData } from '@alltrails/explore-service/redux/exploreServiceSlice';
import AdminExploreFilters, {
  AdminExploreFilterLocation,
  AdminExploreFilterLocationType
} from '@alltrails/explore-service/types/AdminExploreFilters';
import type DistanceAway from '@alltrails/explore-service/types/DistanceAway';
import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import type ExploreServiceSort from '@alltrails/explore-service/types/ExploreServiceSort';
import { defaultExploreServiceSort } from '@alltrails/explore-service/utils/constants';
import type { SeasonalMonth } from '@alltrails/explore-service/utils/seasonalMonth';
import { getCurrentMonth, monthToApiParam, monthToSortParam, sortParamToMonth } from '@alltrails/explore-service/utils/seasonalMonth';
import {
  activityParamKey,
  adminAreaParamKey,
  adminCityParamKey,
  adminClosedParamKey,
  adminCountryParamKey,
  adminStateParamKey,
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  createDistanceAwayParamString,
  difficultyParamKey,
  distanceAwayParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  latParamKey,
  lengthParamKey,
  lngParamKey,
  minRatingParamKey,
  naturalLanguageClickPositionParamKey,
  naturalLanguageQueryParamKey,
  naturalLanguageSearchOriginParamKey,
  naturalLanguageSelectionTypeParamKey,
  parseDistanceAwayParamString,
  routeTypeParamKey,
  sortParamKey,
  suitabilityParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey,
  trailEngagementParamKey,
  trailTrafficParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import useNaturalLanguageSearchExperiment from '@alltrails/layout/hooks/useNaturalLanguageSearchExperiment';
import useMap from '@alltrails/maps/hooks/useMap';
import { TrailTraffic } from '@alltrails/trails';
import { getPlace } from '@/utils/clientRequests';
import { useAppSelector } from '@/utils/redux';

export type ExploreParamCategories = 'boundingBox' | 'distanceAway' | 'filters' | 'naturalLanguageQuery';

// Backwards compatibility for old explore :/
export const trailTrafficParamValues: Record<TrailTraffic, string> = {
  light: '1',
  moderate: '2',
  heavy: '3'
};

const locationTypeParamKeys: Record<AdminExploreFilterLocationType, string> = {
  area: adminAreaParamKey,
  city: adminCityParamKey,
  state: adminStateParamKey,
  country: adminCountryParamKey
};

export const getParamValue = (searchParams: ReadonlyURLSearchParams, key: string): string | null => {
  const value = searchParams.get(key);
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

const getParamValues = (searchParams: ReadonlyURLSearchParams, key: string): string[] => {
  const value = searchParams.getAll(key);
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }
  return [value];
};

const exploreControlledParamKeys = [
  activityParamKey,
  adminAreaParamKey,
  adminCityParamKey,
  adminClosedParamKey,
  adminCountryParamKey,
  adminStateParamKey,
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  difficultyParamKey,
  distanceAwayParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  latParamKey,
  lengthParamKey,
  lngParamKey,
  minRatingParamKey,
  naturalLanguageQueryParamKey,
  naturalLanguageClickPositionParamKey,
  naturalLanguageSelectionTypeParamKey,
  routeTypeParamKey,
  sortParamKey,
  suitabilityParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey,
  trailEngagementParamKey,
  trailTrafficParamKey
];

const useExploreSearchParams = (paramsToInclude?: ExploreParamCategories[]) => {
  const map = useMap();
  const pathname = usePathname();
  const user = useUser();
  const { hasPermission } = useAuthorization();
  const searchParams = useSearchParams();
  const { isNaturalLanguageSearchEnabled } = useNaturalLanguageSearchExperiment();

  const updateUrl = (url: string) => {
    window.history.replaceState(null, '', url);
  };

  const {
    adminFilters,
    boundingBox,
    distanceAway,
    exploreServiceFilters,
    exploreServiceSort,
    naturalLanguageQuery,
    seasonalMonth,
    trailEngagement,
    useMetric
  } = useAppSelector(state => ({
    adminFilters: state.exploreService.adminFilters,
    boundingBox: state.exploreService.boundingBox,
    distanceAway: state.exploreService.distanceAway,
    exploreServiceFilters: state.exploreService.filters,
    exploreServiceSort: state.exploreService.sort,
    naturalLanguageQuery: state.exploreService.naturalLanguageQuery,
    seasonalMonth: state.exploreService.seasonalMonth,
    trailEngagement: state.exploreService.trailEngagement,
    useMetric: state.context.displayMetric
  }));

  useEffect(() => {
    if (!map) {
      // wait until the map is loaded before setting params to avoid conflict with initial load
      return;
    }

    // Rather than trying to figure out what filters have changed when an update occurs,
    // clear and recalculate the new explore-controlled search params.
    // Preserve params that are not controlled by the explore app.
    const searchParamsObject = Object.fromEntries(searchParams.entries());
    const stringifiedParams = stringify(searchParamsObject);
    const newSearchParams = new URLSearchParams(stringifiedParams);
    exploreControlledParamKeys.forEach(key => {
      newSearchParams.delete(key);
    });
    const includeBoundingBox = paramsToInclude?.includes('boundingBox') ?? true;
    const includeDistanceAway = paramsToInclude?.includes('distanceAway') ?? true;
    const includeFilters = paramsToInclude?.includes('filters') ?? true;
    const includeNaturalLanguageQuery = paramsToInclude?.includes('naturalLanguageQuery') ?? true;

    if (boundingBox && includeBoundingBox) {
      newSearchParams.append(bottomRightLatParamKey, String(boundingBox.bottomRight.lat));
      newSearchParams.append(bottomRightLngParamKey, String(boundingBox.bottomRight.lng));
      newSearchParams.append(topLeftLatParamKey, String(boundingBox.topLeft.lat));
      newSearchParams.append(topLeftLngParamKey, String(boundingBox.topLeft.lng));
    }

    if (distanceAway && includeDistanceAway) {
      newSearchParams.append(distanceAwayParamKey, createDistanceAwayParamString(distanceAway));
    }

    if (exploreServiceFilters?.activity?.length && includeFilters) {
      newSearchParams.append(activityParamKey, exploreServiceFilters.activity[0]);
    }

    if (naturalLanguageQuery && includeNaturalLanguageQuery) {
      newSearchParams.append(naturalLanguageQueryParamKey, naturalLanguageQuery);
    }

    if (exploreServiceFilters?.difficulty && includeFilters) {
      exploreServiceFilters.difficulty.map(diff => {
        newSearchParams.append(difficultyParamKey, diff);
      });
    }

    if (exploreServiceFilters?.length && includeFilters) {
      const { min, max } = exploreServiceFilters.length;
      newSearchParams.append(lengthParamKey, String(min ?? 0));
      newSearchParams.append(lengthParamKey, String(max ?? -1));
    }

    if (exploreServiceFilters?.duration && includeFilters) {
      const { min, max } = exploreServiceFilters.duration;
      newSearchParams.append(durationParamKey, String(min ?? 0));
      newSearchParams.append(durationParamKey, String(max ?? -1));
    }

    if (exploreServiceFilters?.elevationGain && includeFilters) {
      const { min, max } = exploreServiceFilters.elevationGain;
      newSearchParams.append(elevationGainParamKey, String(min ?? 0));
      newSearchParams.append(elevationGainParamKey, String(max ?? -1));
    }

    if (exploreServiceFilters?.suitability && includeFilters) {
      exploreServiceFilters.suitability.map(suitability => {
        newSearchParams.append(suitabilityParamKey, suitability);
      });
    }

    if (exploreServiceFilters?.feature && includeFilters) {
      exploreServiceFilters.feature.map(feature => {
        newSearchParams.append(featureParamKey, feature);
      });
    }

    if (exploreServiceFilters?.routeType && includeFilters) {
      exploreServiceFilters.routeType.map(routeType => {
        newSearchParams.append(routeTypeParamKey, routeType);
      });
    }

    if (exploreServiceFilters?.minRating && includeFilters) {
      newSearchParams.append(minRatingParamKey, String(exploreServiceFilters.minRating));
    }

    if (exploreServiceFilters?.trailTraffic && includeFilters) {
      exploreServiceFilters.trailTraffic.map(trailTraffic => {
        newSearchParams.append(trailTrafficParamKey, trailTrafficParamValues[trailTraffic]);
      });
    }

    if (exploreServiceFilters?.highestPoint && includeFilters) {
      const { min, max } = exploreServiceFilters.highestPoint;
      newSearchParams.append(highestPointParamKey, String(min ?? 0));
      newSearchParams.append(highestPointParamKey, String(max ?? -1));
    }

    if (trailEngagement && includeFilters) {
      trailEngagement.map(engagement => {
        newSearchParams.append(trailEngagementParamKey, engagement);
      });
    }

    if (exploreServiceSort && exploreServiceSort !== defaultExploreServiceSort && includeFilters) {
      // Never write 'seasonal' to URL - always use 'month_X' format for consistency
      const sortParamValue = exploreServiceSort === 'seasonal' ? monthToSortParam(seasonalMonth || getCurrentMonth()) : exploreServiceSort;
      newSearchParams.append(sortParamKey, sortParamValue);
    }

    if (hasPermission({ permission: 'trails:manage' }) && adminFilters) {
      if (adminFilters.isClosed !== undefined) {
        newSearchParams.append(adminClosedParamKey, String(adminFilters.isClosed));
      }

      (Object.keys(locationTypeParamKeys) as AdminExploreFilterLocationType[]).forEach(locationType => {
        if (adminFilters[locationType]?.length) {
          const paramKey = locationTypeParamKeys[locationType];
          adminFilters[locationType].forEach(location => {
            newSearchParams.append(paramKey, String(location.id));
          });
        }
      });
    }

    if (newSearchParams.toString() === stringifiedParams) {
      return;
    }

    // decodeURIComponent keeps [] instead of escaping them.
    updateUrl(`${pathname}?${decodeURIComponent(newSearchParams.toString())}`);
  }, [
    adminFilters,
    boundingBox,
    distanceAway,
    exploreServiceFilters,
    exploreServiceSort,
    hasPermission,
    map,
    naturalLanguageQuery,
    paramsToInclude,
    pathname,
    searchParams,
    seasonalMonth,
    trailEngagement
  ]);

  const initialTrailEngagement = useMemo(() => {
    if (!user) {
      return [];
    }
    return getValidTrailEngagements(getParamValues(searchParams, trailEngagementParamKey));
    // We only want to run this on the initial load
  }, []);

  const initialDistanceAway = useMemo<DistanceAway | undefined>(() => {
    if (!user?.featureEntitlements?.includes('distance_away')) {
      return undefined;
    }
    const distanceAwayParamValue = getParamValue(searchParams, distanceAwayParamKey);
    if (!distanceAwayParamValue) {
      return undefined;
    }
    const { center, distance } = parseDistanceAwayParamString(distanceAwayParamValue);
    const validDistance = getValidDistanceAway(distance, useMetric);

    if (!validDistance) {
      return undefined;
    }

    return { center, distance: validDistance };
    // We only want to run this on the initial load
  }, []);

  const initialNaturalLanguageSearchData = useMemo<{ analyticsData?: NaturalLanguageSearchAnalyticData; query?: string } | undefined>(() => {
    if (!isNaturalLanguageSearchEnabled) {
      return undefined;
    }
    const query = getParamValue(searchParams, naturalLanguageQueryParamKey);
    if (!query) {
      return undefined;
    }

    const clickPositionParamValue = getParamValue(searchParams, naturalLanguageClickPositionParamKey);
    const selectionTypeParamValue = getParamValue(searchParams, naturalLanguageSelectionTypeParamKey);
    const searchOriginParamValue = getParamValue(searchParams, naturalLanguageSearchOriginParamKey);

    const analyticsData: NaturalLanguageSearchAnalyticData = {
      clickPosition: clickPositionParamValue ? Number(clickPositionParamValue) : undefined,
      queryLength: query.length,
      searchOrigin: searchOriginParamValue as any,
      selectionType: selectionTypeParamValue as any
    };

    return { analyticsData, query };

    // We only want to run this on the initial load
  }, []);

  const initialSeasonalMonth = useMemo<SeasonalMonth | undefined>(() => {
    const sortParam = getParamValue(searchParams, sortParamKey);

    // Only set seasonal month if there's a seasonal sort parameter
    // This ensures seasonalMonth is only set when explicitly using seasonal sorting
    if (sortParam === 'seasonal' || sortParam?.startsWith('month_')) {
      return sortParamToMonth(sortParam);
    }

    // Return undefined instead of current month when no seasonal sort is active
    // This prevents overriding the default state with an explicit undefined value
    return undefined;
    // We only want to run this on the initial load
  }, []);

  const initialSort = useMemo<ExploreServiceSort | undefined>(() => {
    const sortParam = getParamValue(searchParams, sortParamKey);
    if (sortParam) {
      // If URL has month_X format, convert to 'seasonal' if X is current month
      // This ensures API receives 'seasonal' for current month
      if (sortParam.startsWith('month_')) {
        const month = sortParamToMonth(sortParam);
        return monthToApiParam(month) as ExploreServiceSort;
      }
      return sortParam as ExploreServiceSort;
    }
    return undefined;
    // We only want to run this on the initial load
  }, []);

  const initialFilters = useMemo(() => {
    const filters: ExploreServiceFilters = {};

    const activity = getParamValue(searchParams, activityParamKey);
    if (activity && isValidActivity(activity)) {
      filters.activity = [activity];
    }

    const difficulties = getValidDifficulties(getParamValues(searchParams, difficultyParamKey));
    if (difficulties.length) {
      filters.difficulty = difficulties;
    }

    const lengths = getParamValues(searchParams, lengthParamKey);
    if (lengths.length === 2) {
      const validMin = lengths[0] === '0' ? undefined : getValidLength(Number(lengths[0]), useMetric);
      const validMax = lengths[1] === '-1' ? undefined : getValidLength(Number(lengths[1]), useMetric);

      if (validMin && !validMax) {
        filters.length = { min: validMin };
      } else if (!validMin && validMax) {
        filters.length = { max: validMax };
      } else if (validMin && validMax) {
        filters.length = { min: validMin, max: validMax };
      }
    }

    const durations = getParamValues(searchParams, durationParamKey);
    if (durations.length === 2) {
      const validMin = durations[0] === '0' ? undefined : getValidDuration(Number(durations[0]));
      const validMax = durations[1] === '-1' ? undefined : getValidDuration(Number(durations[1]));

      if (validMin && !validMax) {
        filters.duration = { min: validMin };
      } else if (!validMin && validMax) {
        filters.duration = { max: validMax };
      } else if (validMin && validMax) {
        filters.duration = { min: validMin, max: validMax };
      }
    }

    const elevations = getParamValues(searchParams, elevationGainParamKey);
    if (elevations.length === 2) {
      const validMin = elevations[0] === '0' ? undefined : getValidElevationGain(Number(elevations[0]), useMetric);
      const validMax = elevations[1] === '-1' ? undefined : getValidElevationGain(Number(elevations[1]), useMetric);

      if (validMin && !validMax) {
        filters.elevationGain = { min: validMin };
      } else if (!validMin && validMax) {
        filters.elevationGain = { max: validMax };
      } else if (validMin && validMax) {
        filters.elevationGain = { min: validMin, max: validMax };
      }
    }

    const suitabilities = getValidSuitabilities(getParamValues(searchParams, suitabilityParamKey));
    if (suitabilities.length) {
      filters.suitability = suitabilities;
    }

    const features = getValidFeatures(getParamValues(searchParams, featureParamKey));
    if (features.length) {
      filters.feature = features;
    }

    const routeTypes = getValidRouteTypes(getParamValues(searchParams, routeTypeParamKey));
    if (routeTypes.length) {
      filters.routeType = routeTypes;
    }

    const minRating = getParamValue(searchParams, minRatingParamKey);
    if (minRating && isValidRating(Number(minRating))) {
      filters.minRating = Number(minRating);
    }

    const trailTrafficValues = getParamValues(searchParams, trailTrafficParamKey);
    const trailTraffics = (Object.keys(trailTrafficParamValues) as TrailTraffic[]).filter(key =>
      trailTrafficValues.includes(trailTrafficParamValues[key])
    );
    if (trailTraffics.length) {
      filters.trailTraffic = trailTraffics;
    }

    const highestPoints = getParamValues(searchParams, highestPointParamKey);
    if (highestPoints.length === 2) {
      const validMin = highestPoints[0] === '0' ? undefined : getValidHighestPoint(Number(highestPoints[0]), useMetric);
      const validMax = highestPoints[1] === '-1' ? undefined : getValidHighestPoint(Number(highestPoints[1]), useMetric);

      if (validMin && !validMax) {
        filters.highestPoint = { min: validMin };
      } else if (!validMin && validMax) {
        filters.highestPoint = { max: validMax };
      } else if (validMin && validMax) {
        filters.highestPoint = { min: validMin, max: validMax };
      }
    }

    return filters;
    // We only want to run this on the initial load
  }, []);

  const [initialAdminFilters, setInitialAdminFilters] = useState<AdminExploreFilters>({});
  useEffect(() => {
    if (!hasPermission({ permission: 'trails:manage' })) {
      return;
    }

    try {
      const fetchPlaces = async () => {
        const filters: AdminExploreFilters = {};

        const closed = getParamValue(searchParams, adminClosedParamKey);
        if (closed === 'true') {
          filters.isClosed = true;
        } else if (closed === 'false') {
          filters.isClosed = false;
        }

        for (const locationType of Object.keys(locationTypeParamKeys) as AdminExploreFilterLocationType[]) {
          const values = getParamValues(searchParams, locationTypeParamKeys[locationType]);
          if (values.length) {
            const locations: AdminExploreFilterLocation<typeof locationType>[] = [];
            for (const idString of values) {
              const { place } = await getPlace({ id: Number(idString), placeType: locationType });
              if (place) {
                locations.push({ id: place.ID, name: place.name, type: locationType });
              }
            }

            filters[locationType] = locations;
          }
        }
        setInitialAdminFilters(filters);
      };
      fetchPlaces();
    } catch (error) {
      // Don't need crazy error handling for admins but want to ensure that this doesn't break the whole page
      console.log(error);
    }
    // We only want to run this on the initial load
  }, []);

  return useMemo(
    () => ({
      initialAdminFilters,
      initialDistanceAway,
      initialFilters,
      initialNaturalLanguageSearchData,
      initialSeasonalMonth,
      initialSort,
      initialTrailEngagement
    }),
    [
      initialAdminFilters,
      initialDistanceAway,
      initialFilters,
      initialNaturalLanguageSearchData,
      initialSeasonalMonth,
      initialSort,
      initialTrailEngagement
    ]
  );
};

export default useExploreSearchParams;
